/**
 * ``useAggregators`` — TanStack Query hook around
 * ``GET /api/query/aggregators``.
 *
 * Stream A PR 5.5. Feeds the Entity Details panel's Aggregator
 * section schema per Flow Q + ADR-024 §4. Returned items mirror
 * ``control_plane.aggregators`` rows; the panel filters to the
 * selected aggregator's row.
 *
 * Cross-references:
 *   - REQ-UI-049 (Aggregator section schema).
 *   - REQ-AUTH-024..027.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type AggregatorListResponse = components["schemas"]["AggregatorListResponse"];
export type AggregatorListItem = components["schemas"]["AggregatorListItem"];

export const AGGREGATORS_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

export const AGGREGATORS_STALE_TIME_MS = 30_000;

export function aggregatorsQueryKey(): readonly unknown[] {
  return ["aggregators"] as const;
}

export function useAggregators(): UseQueryResult<AggregatorListResponse, ApiError> {
  const session = useSession();
  const enabled = session.hasAnyRole(AGGREGATORS_READER_ROLES);
  return useQuery<AggregatorListResponse, ApiError>({
    queryKey: aggregatorsQueryKey(),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>("/api/query/aggregators", { signal });
      if (!isAggregatorListResponse(raw)) {
        console.error(
          "[meridian/entity-details/use-aggregators] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "aggregators response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: AGGREGATORS_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

function isAggregatorListResponse(value: unknown): value is AggregatorListResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.items) && typeof obj.total === "number" && typeof obj.duration_ms === "number"
  );
}
